import { useCallback, useContext } from 'react'

import { DialogContext } from './dialog.context'
export const useDialogContext = () => {
  const dialogContextValue = useContext(DialogContext)

  if (!dialogContextValue) {
    throw new Error('useDialog must be used inside a DialogProvider.')
  }

  return dialogContextValue
}

export const useDialog = () => {
  const dialogContextValue = useDialogContext()

  const [{ dialogState, dialogStack }, setState, closeDialog] = dialogContextValue

  const pushDialog = useCallback(
    (newDialog, newDialogProps = {}, newDialogState = {}) => {
      setState((previousState) => ({
        dialogState: {
          ...previousState.dialogState,
          ...newDialogState,
          open: true,
        },
        dialogStack: [
          ...previousState.dialogStack,
          { component: newDialog, props: newDialogProps },
        ],
      }))
    },
    [setState]
  )

  const popDialog = useCallback(
    (newProps = {}, newDialogState = {}) => {
      setState((previousState) => {
        const previousDialog = previousState.dialogStack[previousState.dialogStack.length - 2]
        const previousDialogWithNewProps = {
          ...previousDialog,
          props: { ...previousDialog?.props, ...newProps },
        }
        const newDialogStack = previousDialog
          ? [...previousState.dialogStack.slice(0, -2), previousDialogWithNewProps]
          : []
        return {
          dialogState: {
            ...previousState.dialogState,
            ...newDialogState,
            open: newDialogStack?.length > 0,
          },
          dialogStack: newDialogStack,
        }
      })
    },
    [setState]
  )

  const popDialogs = useCallback(
    (count = 1, newDialogState = {}) => {
      setState((previousState) => {
        if (count <= 0 || previousState.dialogStack.length === 0) {
          return previousState
        }
        const actualCount = Math.min(count, previousState.dialogStack.length)
        const newDialogStack = previousState.dialogStack.slice(0, -actualCount)
        return {
          dialogState: {
            ...previousState.dialogState,
            ...newDialogState,
            open: newDialogStack?.length > 0,
          },
          dialogStack: newDialogStack,
        }
      })
    },
    [setState]
  )

  const popToDialog = useCallback(
    (predicate, newDialogState = {}, newProps = {}, dialogToPush = null) => {
      setState((previousState) => {
        const targetIndex = previousState.dialogStack.findLastIndex(predicate)
        // A closed dialog keeps its stack until the fade-out ends; a late async callback must not
        // reopen it.
        if (targetIndex === -1 || previousState.dialogState.open === false) {
          return previousState
        }
        const targetDialog = previousState.dialogStack[targetIndex]
        const newDialogStack = [
          ...previousState.dialogStack.slice(0, targetIndex),
          { ...targetDialog, props: { ...targetDialog.props, ...newProps } },
          ...(dialogToPush ? [dialogToPush] : []),
        ]
        return {
          dialogState: { ...previousState.dialogState, ...newDialogState, open: true },
          dialogStack: newDialogStack,
        }
      })
    },
    [setState]
  )

  const filterDialogStack = useCallback(
    (propKey, value) => {
      setState((previousState) => {
        const newDialogStack = previousState.dialogStack.filter(
          (dialog) => !!dialog.props?.[propKey] && dialog.props[propKey] === value
        )
        return {
          dialogState: {
            ...previousState.dialogState,
            open: newDialogStack?.length > 0,
          },
          dialogStack: newDialogStack,
        }
      })
    },
    [setState]
  )

  const setDialogState = useCallback(
    (newGlobalState = {}) => {
      setState(({ dialogState, dialogStack }) => ({
        dialogState: { ...dialogState, ...newGlobalState },
        dialogStack,
      }))
    },
    [setState]
  )

  const updateCurrentDialogProps = useCallback(
    (newProps = {}) => {
      setState((previousState) => {
        const currentDialog = previousState.dialogStack[previousState.dialogStack.length - 1]
        if (!currentDialog) {
          return previousState
        }
        const updatedDialog = { ...currentDialog, props: { ...currentDialog.props, ...newProps } }
        return {
          dialogState: previousState.dialogState,
          dialogStack: [...previousState.dialogStack.slice(0, -1), updatedDialog],
        }
      })
    },
    [setState]
  )

  return {
    popDialog,
    popDialogs,
    popToDialog,
    pushDialog,
    setDialogState,
    updateCurrentDialogProps,
    filterDialogStack,
    dialogState,
    closeDialog,
    dialogStack,
  }
}
